/**
 * Browse Card Component
 * Compact card for grid-based browsing
 */

import type { BrowseProfile } from '../api';
import { formatHeight, getUnits } from '../utils';
import { store } from '../state';

/**
 * Render a compact browse card for grid display
 * Shows: photo with name/age/city overlay, info line (job, height, kids)
 */
export function renderBrowseCard(profile: BrowseProfile): string {
  const photos = Array.isArray(profile.photos) ? profile.photos : [];
  const primaryPhoto = photos[0];
  const photoUrl = primaryPhoto?.files.square_medium?.url || primaryPhoto?.files.medium?.url || primaryPhoto?.files.large?.url || '';
  const units = getUnits();

  // Build location for overlay (city only, no distance)
  const locationWithDistance = profile.city || '';

  // Build info parts for two lines (below image)
  // Line 1: job title only (distance moved to overlay)
  // Line 2: height + kids
  const line1Parts: string[] = [];
  const line2Parts: string[] = [];

  // Line 1: Job title
  if (profile.job_title) {
    line1Parts.push(profile.job_title);
  }

  // Line 2: Height
  if (profile.height_cm) {
    line2Parts.push(formatHeight(profile.height_cm, units));
  }

  // Line 2: Kids - extract from profile_json
  const profileJson = profile.profile_json as Record<string, unknown> | null;
  const kids = profileJson?.kids as { has_kids?: boolean | string; children?: unknown[] } | undefined;
  const hasKids = kids?.has_kids;
  if (hasKids === true || hasKids === 'yes') {
    const children = kids?.children;
    if (Array.isArray(children) && children.length > 0) {
      line2Parts.push(`${children.length} kid${children.length > 1 ? 's' : ''}`);
    } else {
      line2Parts.push('Has kids');
    }
  }

  // Render info section with two lines
  const line1Html = line1Parts.length > 0 ? `<div class="browse-card-info-line truncate">${line1Parts.join(' · ')}</div>` : '';
  const line2Html = line2Parts.length > 0 ? `<div class="browse-card-info-line truncate">${line2Parts.join(' · ')}</div>` : '';
  const infoHtml = (line1Html || line2Html)
    ? `<div class="browse-card-info">${line1Html}${line2Html}</div>`
    : '';

  return `
    <div class="browse-card" data-user-id="${profile.user_id}">
      <div class="browse-card-photo">
        ${photoUrl
          ? `<img src="${photoUrl}" alt="${profile.first_name}" loading="lazy" />`
          : `<div class="browse-card-placeholder"><i class="fas fa-user"></i></div>`
        }
        <div class="browse-card-overlay">
          <div class="browse-card-name">${profile.first_name}</div>
          <div class="browse-card-details">
            <span>${[profile.age, locationWithDistance].filter(Boolean).join(' · ')}</span>
            ${profile.user_type === 1 ? '<span class="ai-badge"></span>' : ''}
          </div>
        </div>
      </div>
      ${infoHtml}
    </div>
  `;
}

/**
 * Setup click handlers for browse cards
 * Navigates to profile view on click and stores browse context
 */
export function setupBrowseCardClickHandlers(
  navigate: (path: string) => void,
  profiles?: { user_id: string }[],
  returnTo: string = '/browse'
): void {
  document.querySelectorAll('.browse-card').forEach(card => {
    card.addEventListener('click', () => {
      const userId = (card as HTMLElement).dataset.userId;
      if (userId) {
        // Store browse context if profiles are provided
        if (profiles && profiles.length > 0) {
          const currentIndex = profiles.findIndex(p => p.user_id === userId);
          if (currentIndex >= 0) {
            // Save scroll position from the scrollable grid element
            const grid = document.querySelector('.browse-grid');
            const scrollPosition = grid?.scrollTop || 0;
            store.setBrowseContext({
              profiles,
              currentIndex,
              scrollPosition,
              returnTo,
            });
          }
        }
        navigate(`/u/${userId}`);
      }
    });
  });
}
